import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface Props {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
  maxWidth?: string
}

export default function Modal({
  open,
  onClose,
  title,
  description,
  children,
  maxWidth = 'max-w-lg',
}: Props) {
  const { t } = useTranslation('common')
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  // Portal a document.body: evita que un ancestro con transform/filter/
  // backdrop-filter "atrape" el position:fixed y deje el modal por debajo
  // de otros controles.
  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-3 py-4 md:px-4">
      <div
        className={`w-full ${maxWidth} surface-deep p-4 md:p-6 animate-slideUp max-h-[calc(100vh-2rem)] flex flex-col`}
      >
        <div className="flex items-start justify-between mb-4 shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold t-primary">{title}</h2>
            {description && <p className="text-xs t-muted mt-1">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-3 -mt-1 -mr-1 w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted"
            aria-label={t('actions.close')}
          >
            <X size={18} />
          </button>
        </div>
        <div className="overflow-y-auto -mx-1 px-1">{children}</div>
      </div>
    </div>,
    document.body,
  )
}
